// Interviews (D20, DC-167/169/175/176): the interviewer seat's banded row list of its own rounds. Each row reads the
// round, the candidate, the scorecard status and the Due clock; the acknowledgment sits on the row. The rows come from
// V1 `packet.list_mine` with S04's `scorecard_status`/`due_at` and B6's `ack_status`/`change_status` fields; a field a
// row does not carry yet reads as its V1 equivalent or as nothing, never as a guessed word.

import { packetListMine, type PacketListMineOutput } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import {
  AckPair, Card, Chip, Count, Due, EmptyState, FILTERBAR_LABELS, HM_REVIEW_STATUS, McRoot, SLA_PILL_LABELS, SLA_TIER_LABELS, useViewerMemory, NAV_ITEM_LABELS, PANEL_CHANGE_STATUS, PageHeader, Pill, SCORECARD_STATUS, SLA_LABELS,
  Stack, Table, Td, TextLink, Th, Tr, type AckStatus, type DueTier, type Tone,
} from "@/mc";
import { AckControl } from "@/views/panel/AckControl";
import { SCORE_LABELS } from "./labels";
import { hisRoundLabel } from "./round";

export type ScorecardDisplay = "not_started" | "in_progress" | "submitted" | "past_due" | "waived";

export const DISPLAY_WORD: Readonly<Record<ScorecardDisplay, string>> = {
  not_started: SCORECARD_STATUS.notStarted,
  in_progress: SCORECARD_STATUS.inProgress,
  submitted: SCORECARD_STATUS.submitted,
  past_due: SCORECARD_STATUS.pastDue,
  waived: SCORECARD_STATUS.waived,
};

export const DISPLAY_TONE: Readonly<Record<ScorecardDisplay, Tone>> = {
  not_started: "neutral", in_progress: "info", submitted: "ok", past_due: "red", waived: "neutral",
};

const DISPLAYS = new Set<string>(Object.keys(DISPLAY_WORD));
const V1_SUBMITTED = new Set(["submitted", "frozen", "frozen_with_addenda"]);

/** S04's display status when the row carries it; otherwise V1's scorecard status read through the same five words. */
export function displayOf(row: Record<string, unknown>): ScorecardDisplay {
  for (const key of ["display_status", "scorecard_status"]) {
    const value = row[key];
    if (typeof value === "string" && DISPLAYS.has(value)) return value as ScorecardDisplay;
  }
  const v1 = typeof row["scorecard_status"] === "string" ? row["scorecard_status"] : typeof row["status"] === "string" ? row["status"] : "";
  if (V1_SUBMITTED.has(v1)) return "submitted";
  if (v1 === "draft") return "in_progress";
  return "not_started";
}

const TIERS = new Set<string>(["due", "reminder", "nearBreach", "overdue", "pastDue"]);

/**
 * The Due pill's tier (DC-19). An open card past its due time is Past Due; otherwise the server's tier when the row
 * names one (the reminder and near-breach points are configuration, never a literal here), else the plain due tier.
 * A submitted or waived card, or one with no due time yet, has no clock.
 */
export function dueTierOf(display: ScorecardDisplay, row: Record<string, unknown>): DueTier | null {
  if (display === "submitted" || display === "waived") return null;
  if (display === "past_due") return "pastDue";
  if (typeof row["due_at"] !== "string") return null;
  const tier = row["due_tier"];
  return typeof tier === "string" && TIERS.has(tier) ? (tier as DueTier) : "due";
}

const ACKS = new Set<string>(["pending", "nonresponse", "accepted", "declined"]);
const PANEL_CHANGE_KEY: Readonly<Record<string, keyof typeof PANEL_CHANGE_STATUS>> = {
  draft: "draft", submitted: "submitted", pending_recruiter_review: "pendingRecruiterReview", pending_hm_review: "pendingHmReview",
  blocked: "blocked", approved: "approved", rejected: "rejected", communicated: "communicated", completed: "completed",
};

export interface InterviewRowView {
  key: string;
  packetId: string;
  applicationId: string;
  candidate: string;
  requisitionTitle: string;
  roundLabel: string;
  display: ScorecardDisplay;
  dueAt: string | null;
  dueTier: DueTier | null;
  ackStatus: AckStatus | null;
  changeWord: string | null;
  own: boolean;
  /** B6's acknowledgment on this seat, with what its control needs (absent on a row B6 does not carry). */
  ackId?: string | null;
  requisitionId?: string | null;
  nonresponse?: boolean | null;
  changeStatus?: string | null;
  interviewStart?: string | null;
  /** When the round was held (F-181's *Held* filter). */
  heldAt?: string | null;
}

const str = (value: unknown): string | null => (typeof value === "string" && value ? value : null);

/** One row per interview of every packet the viewer holds; own is the viewer's seat on the interview. */
export function interviewRows(data: PacketListMineOutput, viewerId: string | null): InterviewRowView[] {
  return data.items.flatMap((packet) => packet.interviews.map((interview) => {
    const display = displayOf(interview);
    const holder = str(interview["interviewer_id"]);
    const ack = str(packet.ack_status);
    const change = str(packet.change_status);
    const changeKey = change ? PANEL_CHANGE_KEY[change] : undefined;
    return {
      key: `${packet.packet_id}:${String(interview["interview_id"] ?? interview["application_id"])}`,
      // the series id, as V1 routes a packet (`#packets/<series>`): the versioned id carries a "/" and would split the route
      packetId: packet.series_id,
      applicationId: String(interview["application_id"] ?? ""),
      candidate: String(interview["candidate"] ?? ""),
      requisitionTitle: packet.requisition_title,
      roundLabel: hisRoundLabel(packet.round_label),
      display,
      dueAt: str(interview["due_at"]),
      dueTier: dueTierOf(display, interview),
      ackStatus: ack && ACKS.has(ack) ? (ack as AckStatus) : null,
      changeWord: changeKey ? PANEL_CHANGE_STATUS[changeKey] : null,
      own: holder === null || holder === viewerId,
      ackId: str(packet.ack_id),
      requisitionId: packet.requisition_id,
      nonresponse: packet.nonresponse === true,
      changeStatus: change,
      interviewStart: str(interview["start_at"]),
      heldAt: str(interview["held_at"]),
    };
  }));
}

/** DC-167's bands: the open cards first (Past Due, then by due time), then the submitted and waived ones. */
export function bandRows(rows: readonly InterviewRowView[]): { open: InterviewRowView[]; done: InterviewRowView[] } {
  const open = rows.filter((row) => row.display !== "submitted" && row.display !== "waived");
  const done = rows.filter((row) => row.display === "submitted" || row.display === "waived");
  open.sort((a, b) => Number(b.display === "past_due") - Number(a.display === "past_due") || (a.dueAt ?? "~").localeCompare(b.dueAt ?? "~"));
  return { open, done };
}

/** The acknowledgment cell: B6's control on the viewer's own Pending row, its answer read-only everywhere else. */
export function AckCell({ row }: { row: InterviewRowView }) {
  if (!row.ackStatus) return null;
  if (row.own && row.ackStatus === "pending") {
    return (
      <span data-ack-mount={row.key}>
        {row.ackId && row.requisitionId ? (
          /* B6's AckControl: Accept · Decline on this row, Decline opening B6's DeclineDialog (DC-169, DC-175). */
          <AckControl
            acknowledgmentId={row.ackId}
            requisitionId={row.requisitionId}
            status={row.ackStatus}
            nonresponse={row.nonresponse}
            roundLabel={row.roundLabel}
            interviewStart={row.interviewStart}
            changeStatus={row.changeStatus}
          />
        ) : (
          <AckPair status="pending" mode="view" />
        )}
      </span>
    );
  }
  return <AckPair status={row.ackStatus} mode="view" />;
}

export function InterviewRow({ row, index }: { row: InterviewRowView; index: number }) {
  return (
    <Tr >
      <Td label={SCORE_LABELS.candidate}>
        <TextLink href={`#interviews/${row.packetId}/${row.applicationId}`} >{row.candidate}</TextLink>
      </Td>
      <Td label={SCORE_LABELS.round} sub={row.requisitionTitle}>
        <TextLink href={`#interviews/${row.packetId}`} hs={index === 0 ? "score.packet" : undefined}>{row.roundLabel}</TextLink>
      </Td>
      <Td label={SCORE_LABELS.scorecard}>
        <Pill tone={DISPLAY_TONE[row.display]}>{DISPLAY_WORD[row.display]}</Pill>
        {row.changeWord && <Pill tone="violet">{row.changeWord}</Pill>}
      </Td>
      <Td label={SLA_LABELS.scorecardDue} nowrap>
        {row.dueTier && row.dueAt && <Due tier={row.dueTier} phrase={SLA_LABELS.scorecardDue} at={row.dueAt} />}
      </Td>
      <Td label={SCORE_LABELS.acknowledgment}><AckCell row={row} /></Td>
    </Tr>
  );
}

export function InterviewsTable({ rows }: { rows: readonly InterviewRowView[] }) {
  const { open, done } = bandRows(rows);
  const band = (title: string, list: InterviewRowView[], offset: number) => list.length === 0 ? null : (
    <tbody key={title}>
      <Tr><Th row>{title}</Th><Td colSpan={4}>{null}</Td></Tr>
      {list.map((row, i) => <InterviewRow key={row.key} row={row} index={offset + i} />)}
    </tbody>
  );
  return (
    <Table
      label={NAV_ITEM_LABELS.interviews}
      head={<Tr><Th>{SCORE_LABELS.candidate}</Th><Th>{SCORE_LABELS.round}</Th><Th>{SCORE_LABELS.scorecard}</Th><Th>{SLA_LABELS.scorecardDue}</Th><Th>{SCORE_LABELS.acknowledgment}</Th></Tr>}
      bodies={[band(SLA_LABELS.scorecardDue, open, 0), band(SCORECARD_STATUS.submitted, done, open.length)]}
      cols={5}
    />
  );
}

/** F-181: the status filters over the rows, from the states each row carries (its scorecard display status, its Due
 * clock, the round's held time). Due is an open card whose clock has not run out; Overdue one whose clock has (Overdue or
 * Past Due); Submitted the submitted cards; Held a held round whose card is not yet submitted. */
export type InterviewFilter = "all" | "due" | "overdue" | "submitted" | "held";
export const INTERVIEW_FILTERS: readonly InterviewFilter[] = ["all", "due", "overdue", "submitted", "held"];
export const FILTER_WORD: Readonly<Record<InterviewFilter, string>> = {
  all: FILTERBAR_LABELS.all, due: SLA_TIER_LABELS.due, overdue: SLA_PILL_LABELS.overdue, submitted: SCORECARD_STATUS.submitted, held: HM_REVIEW_STATUS.held,
};
const LATE = new Set<string>(["overdue", "pastDue"]);

export function matchesFilter(row: InterviewRowView, filter: InterviewFilter): boolean {
  const open = row.display !== "submitted" && row.display !== "waived";
  switch (filter) {
    case "due": return open && row.display !== "past_due" && !(row.dueTier && LATE.has(row.dueTier));
    case "overdue": return open && (row.display === "past_due" || (row.dueTier !== null && LATE.has(row.dueTier)));
    case "submitted": return row.display === "submitted";
    case "held": return open && Boolean(row.heldAt);
    default: return true;
  }
}

export function InterviewFilters({ rows, value, onChange }: { rows: readonly InterviewRowView[]; value: InterviewFilter; onChange(next: InterviewFilter): void }) {
  return (
    <div className="mc-chips" role="group" aria-label={SCORE_LABELS.scorecard}>
      {INTERVIEW_FILTERS.map((filter) => (
        <Chip key={filter} sm on={value === filter} onToggle={() => onChange(filter)} >
          {FILTER_WORD[filter]} <Count n={rows.filter((row) => matchesFilter(row, filter)).length} />
        </Chip>
      ))}
    </div>
  );
}

function InterviewsBody() {
  const { me } = useSession();
  const list = useRead(["packets", "mine"], () => packetListMine({}));
  const rows = list.data ? interviewRows(list.data, me?.person_id ?? null) : [];
  // the choice is remembered per viewer (this browser, this person)
  const [kept, setFilter] = useViewerMemory<string>(me?.person_id ? `interviews.filter.${me.person_id}` : null, "all");
  const filter = (INTERVIEW_FILTERS as readonly string[]).includes(kept) ? (kept as InterviewFilter) : "all";
  const shown = rows.filter((row) => matchesFilter(row, filter));
  return (
    <Stack gap={16}>
      <PageHeader title={NAV_ITEM_LABELS.interviews} />
      {rows.length ? <InterviewFilters rows={rows} value={filter} onChange={setFilter} /> : null}
      <Card loading={list.isLoading ? true : undefined}>
        {shown.length === 0 && !list.isLoading ? <EmptyState icon="calendar">{NAV_ITEM_LABELS.interviews}</EmptyState> : <InterviewsTable rows={shown} />}
      </Card>
    </Stack>
  );
}

export function Interviews() {
  return <McRoot><InterviewsBody /></McRoot>;
}
